/**
 * Centralized API Routes Registry
 * 
 * This file provides a single source of truth for all API routes used in the frontend.
 * It ensures type safety, prevents typos, and makes it easy to discover available endpoints.
 * 
 * Usage:
 * ```typescript
 * import { API_ROUTES } from '@/lib/api-routes';
 * 
 * // Instead of: fetch('/api/orders')
 * fetch(API_ROUTES.oms.orders.list());
 * 
 * // With parameters: fetch('/api/orders/123')
 * fetch(API_ROUTES.oms.orders.get('123'));
 * ```
 * 
 * Benefits:
 * - Type-safe route construction
 * - Autocomplete for all routes
 * - Easy refactoring (find all usages)
 * - Prevents 404s from typos
 * - Documents which routes exist
 */

const API_BASE = '/api';

/**
 * Build a URL with query parameters
 */
function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) {
    return path;
  }
  
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
  
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * API Routes organized by feature domain (matching backend routers)
 */
export const API_ROUTES = {
  /**
   * Accounts Router - Chart of Accounts (COA) management
   */
  accounts: {
    list: (activeOnly = true) => activeOnly ? `${API_BASE}/accounts` : `${API_BASE}/accounts/all`,
    get: (id: string) => `${API_BASE}/accounts/${id}`,
    create: () => `${API_BASE}/accounts`,
    update: (id: string) => `${API_BASE}/accounts/${id}`,
    delete: (id: string) => `${API_BASE}/accounts/${id}`,
    reactivate: (id: string) => `${API_BASE}/accounts/${id}/reactivate`,
    rollupBalance: (id: string) => `${API_BASE}/accounts/${id}/rollup-balance`,
    rollupByCategory: () => `${API_BASE}/accounts/rollup/by-category`,
    parentsByCategory: (category: string) => `${API_BASE}/accounts/parents/${category}`,
  },

  /**
   * Audit Router - Audit log viewing and filtering
   */
  audit: {
    logs: (params?: { actor?: string; action?: string; startDate?: string; endDate?: string; limit?: number }) =>
      buildUrl(`${API_BASE}/audit-logs`, params),
  },

  /**
   * Feedback Router - User feedback and bug reports
   */
  feedback: {
    submit: () => `${API_BASE}/feedback`,
  },

  /**
   * Config Router - System configuration
   */
  config: {
    shippingProfiles: {
      list: () => `${API_BASE}/config/shipping-profiles`,
      create: () => `${API_BASE}/config/shipping-profiles`,
      update: (id: string) => `${API_BASE}/config/shipping-profiles/${id}`,
      rename: (id: string) => `${API_BASE}/config/shipping-profiles/${id}`,
      delete: (id: string) => `${API_BASE}/config/shipping-profiles/${id}`,
      supplies: {
        add: (profileId: string) => `${API_BASE}/config/shipping-profiles/${profileId}/supplies`,
        remove: (profileId: string, supplyId: string) => 
          `${API_BASE}/config/shipping-profiles/${profileId}/supplies/${supplyId}`,
      },
    },
    salesPlatforms: {
      list: (params?: { include_inactive?: boolean }) => 
        buildUrl(`${API_BASE}/config/sales-platforms`, params),
      create: () => `${API_BASE}/config/sales-platforms`,
      update: (id: string) => `${API_BASE}/config/sales-platforms/${id}`,
      toggleActive: (id: string) => `${API_BASE}/config/sales-platforms/${id}`,
    },
  },

  /**
   * Coupons Router - Coupon management
   */
  coupons: {
    list: (params?: { active?: boolean }) => buildUrl(`${API_BASE}/coupons`, params),
    get: (id: string) => `${API_BASE}/coupons/${id}`,
    create: () => `${API_BASE}/coupons`,
    update: (id: string) => `${API_BASE}/coupons/${id}`,
    delete: (id: string) => `${API_BASE}/coupons/${id}`,
  },

  /**
   * Dashboard Router - KPIs and dashboard metrics
   */
  dashboard: {
    operationalKpis: (params?: { period?: string }) => 
      buildUrl(`${API_BASE}/dashboard/operational-kpis`, params),
    kpis: (params?: { period?: string }) => 
      buildUrl(`${API_BASE}/dashboard/kpis`, params),
    incomeBreakdown: (params?: { period?: string }) => 
      buildUrl(`${API_BASE}/dashboard/income-breakdown`, params),
    ordersDue: () => `${API_BASE}/dashboard/orders-due`,
    lowStock: () => `${API_BASE}/dashboard/low-stock`,
  },

  /**
   * Etsy Router - Etsy integration and import
   */
  etsy: {
    config: () => `${API_BASE}/etsy/config`,
    status: () => `${API_BASE}/etsy/status`,
    auth: {
      start: () => `${API_BASE}/etsy/auth/start`,
      callback: () => `${API_BASE}/etsy/auth/callback`,
      disconnect: () => `${API_BASE}/etsy/auth/disconnect`,
    },
    shop: () => `${API_BASE}/etsy/shop`,
    shopId: () => `${API_BASE}/etsy/shop-id`,
    receipts: (params?: { limit?: number; wasPaid?: boolean }) =>
      buildUrl(`${API_BASE}/etsy/api/receipts`, params),
    csv: {
      parse: () => `${API_BASE}/etsy/csv/parse`,
      import: () => `${API_BASE}/etsy/csv/import`,
      suggestMappings: {
        orders: () => `${API_BASE}/etsy/csv/suggest-mappings/orders`,
        products: () => `${API_BASE}/etsy/csv/suggest-mappings/products`,
        customers: () => `${API_BASE}/etsy/csv/suggest-mappings/customers`,
        coupons: () => `${API_BASE}/etsy/csv/suggest-mappings/coupons`,
      },
    },
  },

  /**
   * Import Router - CSV import for transactions
   */
  import: {
    parse: () => `${API_BASE}/import/parse`,
    validate: () => `${API_BASE}/import/validate`,
    execute: () => `${API_BASE}/import/execute`,
    history: () => `${API_BASE}/import/history`,
    mappings: () => `${API_BASE}/import/mappings`,
  },

  /**
   * Inventory Router - Workshop inventory management
   */
  inventory: {
    filament: {
      list: (params?: { material_type?: string; color?: string; low_stock?: string; include_inactive?: string }) => 
        buildUrl(`${API_BASE}/inventory/filament`, params),
      get: (id: string) => `${API_BASE}/inventory/filament/${id}`,
      create: () => `${API_BASE}/inventory/filament`,
      update: (id: string) => `${API_BASE}/inventory/filament/${id}`,
      delete: (id: string) => `${API_BASE}/inventory/filament/${id}`,
      materialTypes: () => `${API_BASE}/inventory/filament-material-types`,
    },
    purchaseHistory: {
      list: () => `${API_BASE}/inventory/purchase-history`,
      forFilament: (filamentId: string) => `${API_BASE}/inventory/purchase-history/filament/${filamentId}`,
      get: (purchaseId: string) => `${API_BASE}/inventory/purchase-history/${purchaseId}`,
      create: () => `${API_BASE}/inventory/purchase-history`,
      update: (purchaseId: string) => `${API_BASE}/inventory/purchase-history/${purchaseId}`,
    },
    shoppingList: {
      list: (params?: { status?: string }) => 
        buildUrl(`${API_BASE}/inventory/shopping-list`, params),
      create: () => `${API_BASE}/inventory/shopping-list`,
      update: (id: string) => `${API_BASE}/inventory/shopping-list/${id}`,
      markPurchased: (id: string) => `${API_BASE}/inventory/shopping-list/${id}/mark-purchased`,
      archive: (id: string) => `${API_BASE}/inventory/shopping-list/${id}/archive`,
      delete: (id: string) => `${API_BASE}/inventory/shopping-list/${id}`,
    },
    shippingSupplies: {
      list: () => `${API_BASE}/inventory/shipping-supplies`,
      create: () => `${API_BASE}/inventory/shipping-supplies`,
      update: (id: string) => `${API_BASE}/inventory/shipping-supplies/${id}`,
    },
    modelSupplies: {
      list: () => `${API_BASE}/inventory/model-supplies`,
      create: () => `${API_BASE}/inventory/model-supplies`,
      update: (id: string) => `${API_BASE}/inventory/model-supplies/${id}`,
    },
  },

  /**
   * Journal Router - Journal entries and accounting
   */
  journal: {
    list: (params?: { status?: 'draft' | 'posted' }) => 
      buildUrl(`${API_BASE}/journal`, params),
    get: (id: string) => `${API_BASE}/journal/${id}`,
    create: () => `${API_BASE}/journal`,
    update: (id: string) => `${API_BASE}/journal/${id}`,
    delete: (id: string) => `${API_BASE}/journal/${id}`,
    post: (id: string) => `${API_BASE}/journal/${id}/post`,
    reverse: (id: string) => `${API_BASE}/journal/${id}/reverse`,
    balanceCheck: (id: string) => `${API_BASE}/journal/${id}/balance-check`,
    reconcile: (id: string) => `${API_BASE}/journal/${id}/reconcile`,
    reconciliationReport: (params?: { accountId?: string; status?: string }) =>
      buildUrl(`${API_BASE}/reconciliation-report`, params),
    lines: {
      reconcile: (lineId: string) => `${API_BASE}/journal-lines/${lineId}/reconcile`,
    },
    attachments: {
      list: (entryId: string) => `${API_BASE}/journal/${entryId}/attachments`,
      upload: (entryId: string) => `${API_BASE}/journal/${entryId}/attachments`,
      delete: (attachmentId: string) => `${API_BASE}/journal/attachments/${attachmentId}`,
    },
  },

  /**
   * Maintenance Router - Maintenance logs and repair items
   */
  maintenance: {
    logs: {
      list: () => `${API_BASE}/maintenance-logs`,
      create: () => `${API_BASE}/maintenance-logs`,
    },
    tasks: {
      list: (params?: { printer_id?: string }) => 
        buildUrl(`${API_BASE}/maintenance-tasks`, params),
    },
    repairItems: {
      list: () => `${API_BASE}/repair-items`,
      get: (id: string) => `${API_BASE}/repair-items/${id}`,
      create: () => `${API_BASE}/repair-items`,
      update: (id: string) => `${API_BASE}/repair-items/${id}`,
      delete: (id: string) => `${API_BASE}/repair-items/${id}`,
      addToShoppingList: (id: string) => `${API_BASE}/repair-items/${id}/add-to-shopping-list`,
    },
  },

  /**
   * Mileage Router - Mileage tracking and reimbursement
   */
  mileage: {
    logs: {
      list: () => `${API_BASE}/mileage/logs`,
      create: () => `${API_BASE}/mileage/logs`,
      update: (id: string) => `${API_BASE}/mileage/logs/${id}`,
      delete: (id: string) => `${API_BASE}/mileage/logs/${id}`,
    },
    currentRate: () => `${API_BASE}/mileage/current-rate`,
  },

  /**
   * OMS Router - Order Management System
   */
  oms: {
    customers: {
      list: (params?: { search?: string; active?: boolean }) => 
        buildUrl(`${API_BASE}/oms/customers`, params),
      get: (id: string) => `${API_BASE}/oms/customers/${id}`,
      create: () => `${API_BASE}/oms/customers`,
      update: (id: string) => `${API_BASE}/oms/customers/${id}`,
      delete: (id: string) => `${API_BASE}/oms/customers/${id}`,
      deactivate: (id: string) => `${API_BASE}/oms/customers/${id}/deactivate`,
      reactivate: (id: string) => `${API_BASE}/oms/customers/${id}/reactivate`,
      platformIds: {
        list: (customerId: string) => `${API_BASE}/oms/customers/${customerId}/platform-ids`,
        add: (customerId: string) => `${API_BASE}/oms/customers/${customerId}/platform-ids`,
        delete: (customerId: string, platform: string) => `${API_BASE}/oms/customers/${customerId}/platform-ids/${platform}`,
      },
    },
    products: {
      list: (params?: { search?: string; active?: boolean }) => 
        buildUrl(`${API_BASE}/oms/products`, params),
      get: (id: string) => `${API_BASE}/oms/products/${id}`,
      create: () => `${API_BASE}/oms/products`,
      update: (id: string) => `${API_BASE}/oms/products/${id}`,
      delete: (id: string) => `${API_BASE}/oms/products/${id}`,
      variations: {
        list: (productId: string, params?: { include_inactive?: boolean }) =>
          buildUrl(`${API_BASE}/oms/products/${productId}/variations`, params),
        get: (productId: string, variationId: string) => 
          `${API_BASE}/oms/products/${productId}/variations/${variationId}`,
        create: (productId: string) => `${API_BASE}/oms/products/${productId}/variations`,
        update: (productId: string, variationId: string) => 
          `${API_BASE}/oms/products/${productId}/variations/${variationId}`,
        delete: (productId: string, variationId: string) => 
          `${API_BASE}/oms/products/${productId}/variations/${variationId}`,
      },
      supplies: {
        list: (productId: string) => `${API_BASE}/oms/products/${productId}/supplies`,
        create: (productId: string) => `${API_BASE}/oms/products/${productId}/supplies`,
        update: (productId: string, productSupplyId: string) => 
          `${API_BASE}/oms/products/${productId}/supplies/${productSupplyId}`,
        delete: (productId: string, productSupplyId: string) => 
          `${API_BASE}/oms/products/${productId}/supplies/${productSupplyId}`,
      },
    },
    orders: {
      list: (params?: { status?: string; customer?: string }) => 
        buildUrl(`${API_BASE}/oms/orders`, params),
      get: (id: string) => `${API_BASE}/oms/orders/${id}`,
      create: () => `${API_BASE}/oms/orders`,
      update: (id: string) => `${API_BASE}/oms/orders/${id}`,
      updateDerivedStatus: (id: string) => `${API_BASE}/oms/orders/${id}/status`,
      delete: (id: string) => `${API_BASE}/oms/orders/${id}`,
    },
    orderStatuses: {
      list: () => `${API_BASE}/oms/order-statuses`,
      create: () => `${API_BASE}/oms/order-statuses`,
      update: (id: string) => `${API_BASE}/oms/order-statuses/${id}`,
      delete: (id: string) => `${API_BASE}/oms/order-statuses/${id}`,
      reorder: () => `${API_BASE}/oms/order-statuses/reorder`,
    },
    orderItems: {
      assignPrinter: (id: string) => `${API_BASE}/oms/order-items/${id}/printer`,
      updateStatus: (id: string) => `${API_BASE}/oms/order-items/${id}/status`,
    },
    kanbanBoard: {
      list: (params?: { item_type?: string; printer_id?: string }) => 
        buildUrl(`${API_BASE}/oms/kanban-board`, params),
    },
  },

  /**
   * Periods Router - Accounting period management
   */
  periods: {
    list: () => `${API_BASE}/periods`,
    close: (period: string) => `${API_BASE}/periods/${period}/close`,
    reopen: (period: string) => `${API_BASE}/periods/${period}/close`,
    closeYear: (year: string) => `${API_BASE}/periods/${year}/close-year`,
  },

  /**
   * Phases Router - Order phase management
   */
  phases: {
    list: () => `${API_BASE}/phases`,
    get: (id: string) => `${API_BASE}/phases/${id}`,
    create: () => `${API_BASE}/phases`,
    update: (id: string) => `${API_BASE}/phases/${id}`,
    delete: (id: string) => `${API_BASE}/phases/${id}`,
    reorder: () => `${API_BASE}/phases/reorder`,
  },

  /**
   * Printers Router - 3D printer management
   */
  printers: {
    list: (params?: { includeInactive?: boolean }) => 
      buildUrl(`${API_BASE}/printers`, params),
    get: (id: string) => `${API_BASE}/printers/${id}`,
    create: () => `${API_BASE}/printers`,
    update: (id: string) => `${API_BASE}/printers/${id}`,
    delete: (id: string, hard = false) => 
      hard ? `${API_BASE}/printers/${id}?hard=true` : `${API_BASE}/printers/${id}`,
    updateMaintenanceDate: (id: string) => `${API_BASE}/printers/${id}/maintenance-date`,
    ams: {
      updateSlot: (printerId: string, unit: number, slot: number) => 
        `${API_BASE}/printers/${printerId}/ams/${unit}/${slot}`,
    },
    spools: () => `${API_BASE}/printers/spools`,
  },

  /**
   * Print Queue Router - Print job queue management
   */
  printQueue: {
    list: () => `${API_BASE}/print-queue`,
    get: (id: string) => `${API_BASE}/print-queue/${id}`,
    create: () => `${API_BASE}/print-queue`,
    update: (id: string) => `${API_BASE}/print-queue/${id}`,
    assign: (id: string) => `${API_BASE}/print-queue/${id}/assign`,
  },

  /**
   * Profiles Router - User profile management
   */
  profile: {
    get: () => `${API_BASE}/profile`,
    update: () => `${API_BASE}/profile`,
    uploadPicture: () => `${API_BASE}/profile/upload-picture`,
    loginHistory: (params?: { limit?: number }) => 
      buildUrl(`${API_BASE}/profile/login-history`, params),
  },

  /**
   * RBAC Router - Role-based access control
   */
  rbac: {
    roles: {
      list: () => `${API_BASE}/rbac/roles`,
      permissions: (roleId: string) => `${API_BASE}/rbac/roles/${roleId}/permissions`,
      updatePermissions: (roleId: string) => `${API_BASE}/rbac/roles/${roleId}/permissions`,
      create: () => `${API_BASE}/rbac/roles`,
    },
    userRoles: {
      list: () => `${API_BASE}/roles`,
      assign: () => `${API_BASE}/user-roles`,
      remove: (userId: string, roleId: string) => `${API_BASE}/user-roles/${userId}/${roleId}`,
    },
    permissions: {
      my: () => `${API_BASE}/permissions/my-permissions`,
    },
    users: {
      list: () => `${API_BASE}/users`,
    },
  },

  /**
   * Reports Router - Financial reports
   */
  reports: {
    trialBalance: () => `${API_BASE}/trial-balance`,
    balanceCheck: () => `${API_BASE}/balance-check`,
    incomeStatement: (params?: { period?: string }) => 
      buildUrl(`${API_BASE}/income-statement`, params),
    balanceSheet: (params?: { asOf?: string }) => 
      buildUrl(`${API_BASE}/balance-sheet`, params),
    rollup: () => `${API_BASE}/rollup`,
  },

  /**
   * Settings Router - Application settings
   */
  settings: {
    list: () => `${API_BASE}/settings`,
    get: (key: string) => `${API_BASE}/settings/${key}`,
    update: (key: string) => `${API_BASE}/settings/${key}`,
  },

  /**
   * Tracking Router - Time tracking
   */
  tracking: {
    entries: {
      list: () => `${API_BASE}/tracking/entries`,
      create: () => `${API_BASE}/tracking/entries`,
      update: (id: string) => `${API_BASE}/tracking/entries/${id}`,
      byOrder: (orderId: string) => `${API_BASE}/tracking/entries/${orderId}`,
      delete: (id: string) => `${API_BASE}/tracking/entries/${id}`,
    },
  },

  /**
   * Util Router - Utility endpoints
   */
  util: {
    today: () => `${API_BASE}/util/today`,
  },
} as const;

/**
 * Type helper for extracting route return types
 *
 * All route builder functions currently return strings, so we model
 * the route type directly as `string` to avoid brittle indexed access
 * into the nested API_ROUTES object.
 */
export type ApiRoute = string;
